<template>
  <TooltipProvider :delay-duration="350">
    <div id="app" :class="{ 'no-nav': noNav }">
      <RouteLoadingBar />
      <SkipButton @click="focusMainContent">Skip to Main Content</SkipButton>
      <ToastNotifications />
      <div :class="['layout', { 'layout--with-frame': showFrame }]">
        <TheSidebar v-if="showFrame" />
        <div class="layout__content">
          <ConnectionStatusBanner />
          <div class="content-body">
            <div class="content-main">
              <router-view ref="routerView" />
            </div>
            <QuickStartSidebar v-if="showFrame" />
            <FloatingActions />
          </div>
        </div>
      </div>
      <TheFooter v-if="showFooter" />
    </div>
  </TooltipProvider>
</template>

<script>
import { mapState } from "pinia";
import { TooltipProvider } from "reka-ui";

import ConnectionStatusBanner from "@/components-ng/ConnectionStatusBanner.vue";
import FloatingActions from "@/components-ng/FloatingActions.vue";
import RouteLoadingBar from "@/components-ng/RouteLoadingBar.vue";
import TheSidebar from "@/components-ng/TheSidebar.vue";
import ToastNotifications from "@/components-ng/ToastNotifications.vue";
import QuickStartSidebar from "@/components-ng/quickStart/QuickStartSidebar.vue";
import TheFooter from "@/components/TheFooter.vue";
import SkipButton from "@/components/accessibility/SkipButton.vue";
import { useLayout } from "@/composables/useLayout";
import { useAppStore } from "@/stores";

export default {
  name: "App",
  components: {
    ConnectionStatusBanner,
    FloatingActions,
    RouteLoadingBar,
    QuickStartSidebar,
    ToastNotifications,
    TheSidebar,
    TheFooter,
    SkipButton,
    TooltipProvider,
  },
  setup() {
    const { noNav, showFrame } = useLayout();
    return { noNav, showFrame };
  },
  computed: {
    ...mapState(useAppStore, ["onPrem"]),
    showFooter() {
      return ["procurement"].includes(this.$route.name);
    },
  },
  methods: {
    /**
     * Focus the main content area (whatever's inside router-view)
     */
    focusMainContent() {
      const mainElement = this.$refs.routerView?.$el;
      if (mainElement) {
        mainElement.tabIndex = mainElement.tabIndex ?? -1;
        mainElement.focus();
      }
    },
  },
};
</script>

<style lang="scss">
@use "@/colors";
@use "@/colors" as c;
@import "@/variables-legacy";
@import "./styles/fonts.css";
@import "./styles/resets";

html {
  width: 100%;
  -webkit-tap-highlight-color: transparent;
  scrollbar-gutter: stable;

  &:has(.no-nav) {
    scrollbar-gutter: auto;

    body {
      background-color: c.$white;
    }
  }

  body,
  p {
    margin: 0;
    font-size: 0.9375rem;
    line-height: 1.5;
  }

  body {
    background-color: c.$background-grey-light;
    color: $blackColor;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  button {
    box-sizing: border-box;
    cursor: pointer;
  }
}

* {
  font-family: Manrope, sans-serif;
}

#app {
  max-width: $layout-max-width;
  margin: 0 auto;

  @media (width <= 1600px) {
    max-width: 100%;
  }

  &.no-nav {
    max-width: 100%;
    margin: 0;
    min-height: 100vh;
    display: flex;
    flex-direction: column;

    .layout {
      flex: 1;
      min-height: 0;
    }
  }
}

h1,
h2 {
  font-weight: normal;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  display: inline-block;
}

a {
  text-decoration: none;
}

input:not([type="checkbox"], [type="radio"]) {
  appearance: none;
}

html {
  height: 100%;
}

:root {
  --quickstart-sidebar-width: 320px;
}

body,
#app {
  min-height: 100%;
}

.layout {
  display: flex;

  &--with-frame {
    display: grid;
    grid-template-columns: $sidebar-width 1fr;
  }

  &__content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    background-color: $whiteColor;
  }
}

.content-body {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
}

.content-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.no-nav .notifications-list {
  min-width: 350px;
}

.no-nav .notifications-list aside {
  max-width: 250px;
}
</style>
